import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../components/auth/AuthContext';
import AdminTabs from '../../components/admin/AdminTabs';
import { Message } from '../../types/message';
import { APIHOST } from '../../common';

interface MessageWithOrder extends Message {
  order?: {
    id: number;
    documentId: string;
    confirmation?: string;
    contactEmail?: string;
    contactFirstName?: string;
    contactLastName?: string;
  };
}

interface MessagesResponse {
  data: MessageWithOrder[];
  meta: {
    pagination: {
      total: number;
      page: number;
      pageSize: number;
      pageCount: number;
    };
  };
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<MessageWithOrder[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const isAuthenticated = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  useEffect(() => {
    // Check authentication
    if (isAuthenticated) {
      fetchMessages();
    } 
    setLoading(false);
  }, [navigate, isAuthenticated, currentPage, searchQuery]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build search filters
      let searchFilters = '';
      if (searchQuery.trim()) {
        const query = encodeURIComponent(searchQuery.trim());
        searchFilters = `&filters[$or][0][message][$containsi]=${query}&filters[$or][1][author][email][$containsi]=${query}&filters[$or][2][author][firstName][$containsi]=${query}&filters[$or][3][author][lastName][$containsi]=${query}`;
      }

      const response = await fetch(
        `${APIHOST}/api/messages?sort=createdAt:desc&populate[0]=author&populate[1]=order&populate[2]=seenBy&pagination[page]=${currentPage}&pagination[pageSize]=${pageSize}${searchFilters}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data: MessagesResponse = await response.json();
      setMessages(data.data || []);
      setTotalMessages(data.meta.pagination.total);
      setTotalPages(data.meta.pagination.pageCount);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
      console.error('Messages error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchMessages();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    fetchMessages();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getUserDisplayName = (author: Message['author']) => {
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    return author.email;
  };

  const getUserTypeColor = (userType?: string) => {
    switch (userType) {
      case 'admin':
        return 'error';
      case 'internal':
        return 'warning';
      case 'user':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading && !messages.length) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, mt: 8 }}>
      <Container maxWidth="lg">

        <AdminTabs currentPath="/admin/messages" />
        
        <Paper sx={{ p: 3, mt: 3 }}>
          {/* Search Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search messages by content, author email, or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <button 
                type="button"
                onClick={handleSearch}
                disabled={loading}
                style={{ padding: '10px 20px', cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                Search
              </button>
              <button 
                type="button"
                onClick={handleClearSearch}
                disabled={loading}
                style={{ padding: '10px 20px', cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                Clear
              </button>
            </Stack>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Messages List */}
          {!loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {totalMessages} Message{totalMessages !== 1 ? 's' : ''} Found
                {
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    (Page {currentPage} of {totalPages})
                  </Typography>
                }
              </Typography>
              
              {messages.length === 0 ? (
                <Alert severity="info">
                  No messages found. Try adjusting your search criteria.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {messages.map((message) => (
                    <Card key={message.id} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            <PersonIcon />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" component="div">
                                {getUserDisplayName(message.author)}
                              </Typography>
                              <Chip
                                label={message.author.userType || 'user'}
                                size="small"
                                color={getUserTypeColor(message.author.userType)}
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {message.author.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(message.createdAt)}
                            </Typography>
                            <Typography variant="body2" color={message.seenAt ? 'text.secondary' : 'warning.main'}>
                              {message.seenAt ? (
                                `Seen: ${message.seenBy?.firstName} ${message.seenBy?.lastName} ${formatDateTime(message.seenAt)}`
                              ) : (
                                'Unread by user'
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                          {message.message}
                        </Typography>
                        
                        
                        {message.order && (
                          <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              <Link 
                                to={`/admin/order?id=${message.order.documentId}`}
                                style={{ 
                                  textDecoration: 'none', 
                                  color: 'inherit',
                                  cursor: 'pointer'
                                }}
                              >
                                Order: {message.order.confirmation || message.order.documentId}
                                {message.order.contactFirstName && message.order.contactLastName && (
                                  ` - ${message.order.contactFirstName} ${message.order.contactLastName}`
                                )}
                                {message.order.contactEmail && (
                                  ` (${message.order.contactEmail})`
                                )}
                              </Link>
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
              
              {/* Pagination */}
              {
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              }
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}